import { makeAutoObservable } from "mobx";
import {
  ColumnSourceEnum,
  ColumnToDisplay,
  Filtering,
  FilteringRule,
  FilteringRuleENUM,
  PropertyBrowserTreeNode,
  SingleItem,
  SortDirectionENUM,
  Sorting,
} from "api-client";
import { FilteringOption, SortingOption } from "features/MappingEditor/types";
import { BGItem } from "components/ButtonGroups";
import {
  FILTER_VIEW_OPTIONS,
  FilterDefaultGrouping,
} from "features/DynamicTable/features/Filters/Filters";
import { SortProps } from "features/DynamicTable/features/Filters/SortByPropertyBrowser";
import { FilterRule } from "features/DynamicTable/features/Filters/FilterByPropertyBrowser";
import { MEStore } from "features/MappingEditor/MappingEditorStore";
import { dynamicTableStore } from "features/DynamicTable/DynamicTableStore";
import { COLUMNS_GROUPS } from "features/MappingEditor/useTableColumns";

export class FiltersStore {
  selectedGroupByItems: PropertyBrowserTreeNode[] = [];
  selectedFilterByItems: FilteringOption = {
    logical_operator: "AND",
    rules: [],
  };
  selectedSortedByItems: SortProps[] = [];
  openFilter: boolean = false;
  openSort: boolean = false;
  openGroupBy: boolean = false;
  selectedFilterView: BGItem | undefined = FILTER_VIEW_OPTIONS[0];
  validFilterRules: Filtering | null = null;
  validSortingRules: Sorting[] | null = null;
  validGroupByRules: ColumnToDisplay[] = [];
  defaultGrouping: FilterDefaultGrouping = FilterDefaultGrouping.Object;

  constructor() {
    makeAutoObservable(this);
    this.getSortingProps();
  }

  setSelectedGroupByItems(selectedGroupByItems: PropertyBrowserTreeNode[]) {
    this.selectedGroupByItems = selectedGroupByItems;
    this.getGroupingProps();
  }

  setSelectedFilterByItems(selectedFilterByItems: FilteringOption) {
    this.selectedFilterByItems = selectedFilterByItems;
    this.getFilterProps();
  }

  setSelectedSortedByItems(selectedSortedByItems: SortProps[]) {
    this.selectedSortedByItems = selectedSortedByItems;
    this.getSortingProps();
  }

  setOpenFilter(openFilter: boolean) {
    if (openFilter) {
      this.setOpenSort(false);
      this.setOpenGroupBy(false);
      dynamicTableStore.openColumnSettings.open = false;
    }
    this.openFilter = openFilter;
  }

  setOpenSort(openSort: boolean) {
    if (openSort) {
      this.setOpenFilter(false);
      this.setOpenGroupBy(false);
      dynamicTableStore.openColumnSettings.open = false;
    }
    this.openSort = openSort;
  }

  setOpenGroupBy(openGroupBy: boolean) {
    if (openGroupBy) {
      this.setOpenFilter(false);
      this.setOpenSort(false);
      dynamicTableStore.openColumnSettings.open = false;
    }
    this.openGroupBy = openGroupBy;
  }

  setSelectedFilterView(selectedFilterView: BGItem | undefined) {
    this.selectedFilterView = selectedFilterView;
  }

  setdefaultGrouping(defaultGrouping: FilterDefaultGrouping) {
    this.defaultGrouping = defaultGrouping;
  }

  private validFilter(rule: FilterRule) {
    const noValueNeeded = ["IS_EMPTY", "IS_NOT_EMPTY"].includes(rule.operator);
    return noValueNeeded ? !!rule.property : rule.property && rule.value;
  }

  private getGroupingProps() {
    this.validGroupByRules = this.selectedGroupByItems.map((item) => ({
      column_key: item.column_key,
      column_source: item.column_source as ColumnSourceEnum,
    }));
  }

  private getFilterProps() {
    const rules = this.selectedFilterByItems.rules
      .map((item) => {
        if (this.validFilter(item)) {
          let value: string | number =
            item.value && item.value.trim().length > 0 ? item.value : "";
          if (!value && !["IS_EMPTY", "IS_NOT_EMPTY"].includes(item.operator))
            return;
          if (item.property?.is_numeric) {
            value = String(value).replace(",", ".");
            value = parseFloat(value);
          }
          return {
            column_key: item.property?.column_key as string,
            column_source: item.property?.column_source as ColumnSourceEnum,
            operator: item.operator as FilteringRuleENUM,
            value: value,
            name: item.property?.name,
            name_de: item.property?.name_de,
          };
        }
      })
      .filter((rec) => rec);
    if (rules.length) {
      const isEqual =
        this.filterArraysAreEqual(
          rules as FilteringRule[],
          this.validFilterRules?.rules as FilteringRule[]
        ) &&
        this.validFilterRules?.logical_operator ===
          this.selectedFilterByItems.logical_operator;
      if (!isEqual) {
        this.validFilterRules = {
          logical_operator: this.selectedFilterByItems.logical_operator,
          rules: rules as FilteringRule[],
        };
      }
    } else if (this.validFilterRules) this.validFilterRules = null;
  }

  private sortArraysAreEqual(array1: SortProps[], array2: Sorting[] | null) {
    const selectedKeys = array1.map((item) => item.property?.column_key);
    const validKeys = array2?.map((item) => item.column_key);
    const isEqual =
      validKeys &&
      selectedKeys.every(
        (value, index) =>
          value === validKeys[index] &&
          array2?.[index].direction === array1?.[index].sort
      );
    return isEqual && array1.length === array2?.length;
  }

  private filterArraysAreEqual(
    array1: FilteringRule[],
    array2: FilteringRule[] | null
  ) {
    const selectedKeys = array1.map((item) => item?.column_key);
    const validKeys = array2?.map((item) => item.column_key);
    const isEqual =
      validKeys &&
      selectedKeys.every(
        (value, index) =>
          value === validKeys[index] &&
          array2?.[index].value === array1?.[index].value &&
          array2?.[index].operator === array1?.[index].operator
      );
    return isEqual && array1.length === array2?.length;
  }

  private getSortingProps() {
    const isEqual = this.sortArraysAreEqual(
      this.selectedSortedByItems,
      this.validSortingRules
    );
    if (isEqual) return;

    const validatedRows = this.selectedSortedByItems
      .map((item) => {
        if (item.property)
          return {
            column_key: item.property?.column_key,
            column_source: item.property?.column_source as ColumnSourceEnum,
            direction: item.sort as SortDirectionENUM,
          };
      })
      .filter((rec) => rec);
    if (validatedRows.length) {
      this.validSortingRules = validatedRows as SortingOption[];
    } else if (this.validSortingRules) this.validSortingRules = null;
  }

  removeItemFromSortList(id: string) {
    const filteredItems = this.selectedSortedByItems.filter(
      (item) => item.property?.id !== id
    );
    this.setSelectedSortedByItems(filteredItems);
  }

  removeItemFromGoupingList(id: string) {
    const filteredItems = this.selectedGroupByItems.filter(
      (item) => item?.id !== id
    );
    this.setSelectedGroupByItems(filteredItems);
  }

  updateFilterRules(rules: FilterRule[]) {
    this.setSelectedFilterByItems({
      ...this.selectedFilterByItems,
      rules: [...rules],
    });
  }

  removeGroupingRule(id: string) {
    const filteredList = this.selectedGroupByItems.filter(
      (item) => item.id !== id
    );
    this.setSelectedGroupByItems(filteredList);
    this.reOrderGroupingColumns();
  }

  removeAllFilteringRules() {
    this.updateFilterRules([]);
    this.setOpenFilter(false);
  }

  removeAllSortingRules() {
    this.setSelectedSortedByItems([]);
    this.setOpenSort(false);
  }

  removeAllGroupingRules() {
    this.setSelectedGroupByItems([]);
    this.setOpenGroupBy(false);
  }

  reOrderGroupingColumns() {
    const groupingKeys = this.selectedGroupByItems.map((rec) => rec.column_key);
    const informationColumns =
      dynamicTableStore.visibleColumnProperties?.filter(
        (item) => !groupingKeys.includes(item.column_key)
      );
    const newOrder = [...this.selectedGroupByItems, ...informationColumns];
    dynamicTableStore.setVisibleColumnProperties(newOrder);
  }

  hasGrouping() {
    return this.validGroupByRules.length > 0;
  }

  getcurrentGroupKeys(groupName: string) {
    const groupingKeys = this.selectedGroupByItems.map(
      (item) => item.column_key
    );
    const filteredItemsKeys = dynamicTableStore.visibleColumnProperties
      .filter((item) =>
        groupName === COLUMNS_GROUPS.infoColumns
          ? groupingKeys.indexOf(item.column_key) === -1
          : groupingKeys.indexOf(item.column_key) != -1
      )
      .map((rec) => rec.column_key);
    return filteredItemsKeys;
  }

  createCurrentRowGroupSelection(current_object: SingleItem) {
    return [
      {
        logical_operator: "AND",
        rules: this.validGroupByRules.map((item) => ({
          column_key: item.column_key,
          column_source: item.column_source,
          operator: "IS" as FilteringRuleENUM,
          value: current_object[item.column_key as keyof SingleItem],
        })),
      },
    ] as Filtering[];
  }

  createSelectedRowsGroupSelection() {
    const result = MEStore.selectedRows.map((row) => ({
      logical_operator: "AND",
      rules: this.validGroupByRules.map((item) => ({
        column_key: item.column_key,
        column_source: item.column_source,
        operator: "IS" as FilteringRuleENUM,
        // We are not supporting decimals in group selection
        value: String(row.values[item.column_key as keyof typeof row.values]),
      })),
    }));
    return result as Filtering[];
  }

  reset() {
    this.selectedFilterByItems = {
      logical_operator: "AND",
      rules: [],
    };
    this.selectedGroupByItems = [];
    this.selectedSortedByItems = [];
    this.validFilterRules = null;
    this.validSortingRules = null;
    this.validGroupByRules = [];
    this.openFilter = false;
    this.openSort = false;
    this.selectedFilterView = FILTER_VIEW_OPTIONS[0];
  }

  static instance_inventory: FiltersStore;
  static instance_me: FiltersStore;

  static getFilterInventoryStore(): FiltersStore {
    if (!FiltersStore.instance_inventory) {
      FiltersStore.instance_inventory = new FiltersStore();
    }
    return FiltersStore.instance_inventory;
  }

  static getFilterMEStore(): FiltersStore {
    if (!FiltersStore.instance_me) {
      FiltersStore.instance_me = new FiltersStore();
    }
    return FiltersStore.instance_me;
  }
}

export const filterInventoryStore = FiltersStore.getFilterInventoryStore();
export const filterMEStore = FiltersStore.getFilterMEStore();
